import React from 'react';
import { Box, Typography, Stack, CircularProgress, Alert, useTheme } from '@mui/material';
import { useMetricBreakdowns } from '../hooks';
import { getRagColor } from '../utils';
import type { MetricsFilters } from '../api/types';

interface MetricBreakdownPanelProps {
  /** UI metric id, e.g. "sleep_performance" / "strain" / "recovery_score". */
  metricId: string;
  /** Plain-English metric name for the heading, e.g. "Sleep". */
  metricLabel: string;
  /** Unit suffix for values, e.g. "%". */
  unit?: string;
  filterParams: MetricsFilters;
  /** Defer fetching until the panel is opened. */
  enabled?: boolean;
  /** Override the default breakdown dimensions (department/age/gender/shift). */
  dimensions?: ReadonlyArray<{ groupBy: string; label: string }>;
}

/**
 * Inline drill-down showing a metric broken down by department / age / gender /
 * shift, each value colour-coded by its RAG band — e.g. "Sleep → Contact Team
 * 85% (green) / Team B 34% (red)". Reuses the comparison API via
 * useMetricBreakdowns. Metrics the API can't break down render a brief note.
 */
export const MetricBreakdownPanel: React.FC<MetricBreakdownPanelProps> = ({
  metricId,
  metricLabel,
  unit = '',
  filterParams,
  enabled = true,
  dimensions,
}) => {
  const theme = useTheme();
  const {
    supported,
    dimensions: breakdownDimensions,
    isLoading,
  } = useMetricBreakdowns(metricId, filterParams, { enabled, dimensions });

  if (!supported) {
    return (
      <Alert severity='info' sx={{ mt: 1 }}>
        Demographic breakdowns aren't available for {metricLabel.toLowerCase()} yet.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  const dimensionsWithData = breakdownDimensions.filter((d) => d.rows.length > 0);

  if (dimensionsWithData.length === 0) {
    return (
      <Alert severity='info' sx={{ mt: 1 }}>
        No {metricLabel.toLowerCase()} breakdown data for the current filters.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Stack spacing={2}>
        {dimensionsWithData.map((dim) => (
          <Box key={dim.groupBy}>
            <Typography
              variant='caption'
              sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              {dim.label}
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {dim.rows.map((row) => {
                const color = row.ragColor ? getRagColor(row.ragColor, theme) : theme.palette.text.primary;
                return (
                  <Box key={`${dim.groupBy}-${row.label}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {row.ragColor && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          backgroundColor: color,
                        }}
                      />
                    )}
                    <Typography variant='body2' sx={{ flex: 1, minWidth: 0 }} noWrap>
                      {row.label}
                    </Typography>
                    <Typography variant='body2' sx={{ fontWeight: 700, color }}>
                      {row.value}
                      {unit}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
